import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ModeOfTravelOutlinedIcon from '@mui/icons-material/ModeOfTravelOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import {
  alpha,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuProps,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import authService from '../../../services/authService';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as passengerSlice from '../../../data/passenger.slice';
import * as userSlice from '../../../data/user.slice';
import * as uiSlice from '../../../data/ui.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useIsPassengerPortalUser } from '../../../hooks/useIsPassengerPortalUser';
import { useIsClientPortalUser } from '../../../hooks/useIsClientPortalUser';
import { useIsClientPortalAdminUser } from '../../../hooks/useIsClientPortalAdminUser';
import { usePortalName } from '../../../hooks/usePortalName';

const PREFIX = 'Header';

const classes = {
  user: `${PREFIX}-user`,
  line: `${PREFIX}-line`,
  divider: `${PREFIX}-divider`,
};

interface StyledMenuProps extends MenuProps {
  mobile?: boolean;
}

const StyledMenu = styled((props: StyledMenuProps) => {
  const { mobile, ...menuProps } = props;
  const theme = useTheme();
  return (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: mobile
          ? {
              style: {
                transform: `translateX(${theme.spacing(3)})`,
                left: 'auto',
              },
            }
          : undefined,
      }}
      {...menuProps}
    />
  );
})<StyledMenuProps>(({ theme, mobile }) => ({
  '& .MuiPaper-root': {
    borderRadius: mobile ? 0 : 6,
    marginTop: mobile ? theme.spacing(1.5) : theme.spacing(1),
    width: mobile ? 250 : 180,
    padding: mobile ? theme.spacing(2, 0) : 0,
    color: theme.palette.text.secondary,
    boxShadow: theme.shadows[16],
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
      ...(mobile && {
        '&.active': {
          position: 'relative',
          color: theme.palette.text.primary,
          fontWeight: 600,
          '& .MuiSvgIcon-root': {
            color: theme.palette.text.primary,
            fontWeight: 600,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            height: 36,
            width: 3,
            borderRadius: 100,
            backgroundColor: theme.palette.text.primary,
          },
        },
      }),
    },
    ...theme.applyStyles('dark', {
      color: theme.palette.grey[300],
    }),
  },
}));
const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.user}`]: {
    display: 'flex',
    ['&:hover']: {
      opacity: 1,
      cursor: 'pointer',
      '& p': {
        color: theme.palette.navigationTextHoverColor.main,
      },
    },
    '& .MuiSvgIcon-root': {
      padding: theme.spacing(1),
    },
    '& p': {
      color: theme.palette.navigationTextColor.main,
    },
  },
  [`&.${classes.line}`]: {
    width: '1px',
    height: '21px',
    background: theme.palette.secondary.main,
    opacity: 0.25,
    marginLeft: theme.spacing(2),
  },
  [`&.${classes.divider}`]: {
    margin: theme.spacing(0.5, 0),
  },
}));
const StyledTypography = styled(Typography)(({ theme }) => ({
  [`&.${classes.user}`]: {
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.15px',
    alignSelf: 'center',
    color: theme.palette.secondary.main,
  },
}));

function Header() {
  const t = useCommonTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const portalName = usePortalName();
  const user = useAppSelector(userSlice.selectLoggedInUserInfo);
  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const location = useLocation();
  const anchorElForMenuMobile = React.useRef<HTMLButtonElement>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isPassengerPortalUser = useIsPassengerPortalUser();
  const isClientPortalUser = useIsClientPortalUser();
  const isClientAdminUser = useIsClientPortalAdminUser();
  const isClientGeneralUser = !isPassengerPortalUser && !isClientAdminUser;
  const isClientUser = isClientPortalUser || isClientAdminUser;
  const navMenuCollapsed = useAppSelector(uiSlice.selectNavMenuCollapsed);

  const toggleNavMenu = () => {
    dispatch(uiSlice.toggleNavMenu());
  };

  const handleClickMobile = (event: React.MouseEvent<HTMLElement>) => {
    setShowMobileMenu(!showMobileMenu);
  };
  const handleCloseMobile = () => {
    setShowMobileMenu(false);
  };
  const logout = () => {
    authService.logout().then(() => {
      if (isClientPortalUser || isClientAdminUser) {
        navigate('/clientLogin');
      } else {
        navigate('/login');
      }
    });
  };
  const anchorElForMenuDesktop = React.useRef<HTMLButtonElement>(null);
  const [showDesktopMenu, setShowDesktopMenu] = useState(false);
  const handleClickDesktop = (event: React.MouseEvent<HTMLElement>) => {
    setShowDesktopMenu(!showDesktopMenu);
  };
  const handleCloseDesktop = () => {
    setShowDesktopMenu(false);
  };
  React.useEffect(() => {
    if (user?.userAccountPassenger?.Passenger?.passengerUUID) {
      dispatch(
        passengerSlice.fetchPassengerAsync(
          user.userAccountPassenger.Passenger.passengerUUID,
        ),
      );
    }
  }, [user]);
  return (
    <>
      <header>
        <NavLink to="/home">
          <img
            src={`/${portalName}/brand-logo-header.svg`}
            alt={t('member.logoAlt')}
          />
        </NavLink>
        <span className="spacer" />
        <nav className="only-mobile">
          <div className="avatar-section">
            <IconButton
              id="menu-button-mobile"
              aria-label={t('member.openMenu')}
              aria-controls={showMobileMenu ? 'menu-styled-mobile' : undefined}
              aria-haspopup="true"
              aria-expanded={showMobileMenu ? 'true' : undefined}
              onClick={handleClickMobile}
              ref={anchorElForMenuMobile}>
              <MenuOutlinedIcon />
            </IconButton>
            <StyledMenu
              id="menu-styled-mobile"
              mobile={isMobile}
              MenuListProps={{
                'aria-labelledby': 'menu-button-mobile',
              }}
              anchorEl={anchorElForMenuMobile.current}
              open={showMobileMenu}
              onClose={handleCloseMobile}>
              {isPassengerPortalUser &&
                !isClientPortalUser && [
                  <MenuItem
                    key="home"
                    className={location.pathname === '/home' ? 'active' : ''}
                    onClick={() => {
                      handleCloseMobile();
                      navigate('/home');
                    }}
                    disableRipple>
                    <HomeOutlinedIcon />
                    {t('member.home')}
                  </MenuItem>,
                  <MenuItem
                    key="trips"
                    className={location.pathname === '/trips' ? 'active' : ''}
                    onClick={() => {
                      handleCloseMobile();
                      navigate('/trips');
                    }}
                    disableRipple>
                    <ModeOfTravelOutlinedIcon />
                    {t('member.trips')}
                  </MenuItem>,
                ]}
              {/* For mobile, Settings should always be visible for passenger portal users and client admin users, hidden for client general users */}
              {!isClientGeneralUser && (
                <MenuItem
                  className={location.pathname === '/settings' ? 'active' : ''}
                  onClick={() => {
                    handleCloseMobile();
                    navigate('/settings');
                  }}
                  disableRipple>
                  <SettingsOutlinedIcon />
                  {t('member.settings')}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleCloseMobile();
                  logout();
                }}
                disableRipple>
                <LogoutOutlinedIcon />
                {t('member.signout')}
              </MenuItem>
            </StyledMenu>
          </div>
        </nav>
        <nav className="no-mobile">
          <a href="#content" tabIndex={0} className="skip-content-link">
            {t('member.skipToContent')}
          </a>
          {isClientUser && (
            <IconButton
              aria-label={
                navMenuCollapsed ? t('app.showMenu') : t('app.hideMenu')
              }
              onClick={toggleNavMenu}>
              <MenuOutlinedIcon />
            </IconButton>
          )}
          {isPassengerPortalUser && !isClientPortalUser && (
            <>
              <NavLink
                to="/home"
                className={({ isActive, isPending }) =>
                  isActive ? 'active' : isPending ? 'pending' : ''
                }>
                <HomeOutlinedIcon />
                {t('member.home')}
              </NavLink>
              <NavLink
                to="/trips"
                className={({ isActive, isPending }) =>
                  isActive ? 'active' : isPending ? 'pending' : ''
                }>
                <ModeOfTravelOutlinedIcon />
                {t('member.trips')}
              </NavLink>
              <StyledBox className={classes.line} />
            </>
          )}
          <button
            id="menu-button-desktop"
            type="button"
            className="avatar-section"
            onClick={(e) => handleClickDesktop(e)}
            ref={anchorElForMenuDesktop}
            aria-haspopup="true"
            aria-expanded={showDesktopMenu ? 'true' : undefined}
            aria-controls="menu-styled"
            aria-label={user ? 'menu-button-label' : 'menu-button-desktop'}>
            <StyledBox className={classes.user}>
              {user && (
                <StyledTypography
                  id="menu-button-label"
                  className={classes.user}>
                  {`${user.userFirstName} ${user.userLastName}`}
                </StyledTypography>
              )}
              <KeyboardArrowDownIcon />
            </StyledBox>
          </button>
          <StyledMenu
            id="menu-styled"
            MenuListProps={{
              'aria-labelledby': 'menu-button-desktop',
            }}
            anchorEl={anchorElForMenuDesktop.current}
            open={showDesktopMenu}
            onClose={handleCloseDesktop}>
            {/* For desktop, Settings should always be visible for passenger portal users, hidden for client general users */}
            {/* because the sidebar nav disappears at a different breakpoint than the header nav, show Settings in the header for admins between those breakpoints */}
            {(!isClientPortalUser || (isMobile && !isClientGeneralUser)) && [
              <MenuItem
                key="settings"
                className={location.pathname === '/settings' ? 'active' : ''}
                onClick={() => {
                  handleCloseDesktop();
                  navigate('/settings');
                }}
                disableRipple>
                <SettingsOutlinedIcon />
                {t('member.settings')}
              </MenuItem>,
              <Divider key="divider" className="divider" />,
            ]}

            <MenuItem
              onClick={() => {
                handleCloseDesktop();
                logout();
              }}
              disableRipple>
              <LogoutOutlinedIcon />
              {t('member.signout')}
            </MenuItem>
          </StyledMenu>
        </nav>
      </header>
    </>
  );
}

export default Header;
